import { __read } from "tslib";
import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { Box, Flex, Input, Text, IconButton } from '@marvelapp/ui';
import { usePrevious } from '@marvelapp/hooks';
import { ItemUtils } from '@marvelapp/design-creator';
import Highlighter from '../Highlighter';
import { SelectionOption } from '../SelectionList/SelectionOption';
import { CaretDownIcon, LayerClippedArrowIcon } from './icons';
import { LayerIcon } from './Icon';
import { isSymbolLayer, getDefaultLayerLabel, scrollToLayer } from './utils';
function Layer(_a) {
    var _b;
    var filterText = _a.filterText, item = _a.item, isSelected = _a.isSelected, onSelect = _a.onSelect, onMouseEnter = _a.onMouseEnter, onMouseLeave = _a.onMouseLeave, isCollapsed = _a.isCollapsed, onCollapse = _a.onCollapse, isClipped = _a.isClipped;
    var _c = __read(useState(false), 2), isFocus = _c[0], setIsFocus = _c[1];
    var inputRef = useRef(null);
    var selectionRef = useRef(null);
    var previousFilterText = usePrevious(filterText);
    // Scroll selected layer into view if filter is reset
    useLayoutEffect(function () {
        if (previousFilterText && !filterText && isSelected) {
            scrollToLayer(selectionRef.current);
        }
    }, [previousFilterText, filterText, isSelected]);
    // Scroll selected layer into view when it becomes selected
    useLayoutEffect(function () {
        if (isSelected) {
            scrollToLayer(selectionRef.current);
        }
    }, [isSelected]);
    useEffect(function () {
        if (isFocus) {
            inputRef.current.focus();
        }
    }, [isFocus]);
    var handleOnFocus = function (event) {
        event.target.select();
    };
    var handleOnBlur = function () { return setIsFocus(false); };
    var handleOnKeyPress = function (event) {
        if (event.key === 'Enter') {
            setIsFocus(false);
        }
    };
    var handleOnClick = function (event) { return onSelect(item.id, event); };
    var handleOnMouseEnter = function () { return onMouseEnter(item.id); };
    var handleOnMouseLeave = function () { return onMouseLeave(item.id); };
    var name = ItemUtils.getName(item) || getDefaultLayerLabel(item);
    var hasChildren = !!((_b = item.children) === null || _b === void 0 ? void 0 : _b.length);
    var shouldShowCaret = hasChildren;
    var fontSize = '12px';
    var fontWeight = isSelected ? '500' : '400';
    var inputMargin = 4;
    var inputHeight = 30 - inputMargin * 2;
    return (React.createElement(SelectionOption, { "data-selectedoption": isSelected, "data-testid": "layer-item-" + item.id, enableScrollIntoView: true, isSelected: isSelected, onClick: handleOnClick, onMouseEnter: handleOnMouseEnter, onMouseLeave: handleOnMouseLeave, pl: 1, ref: selectionRef, 
        // eslint-disable-next-line no-use-before-define
        styleOverrides: isSymbolLayer(item) && styleOverrides.symbol },
        shouldShowCaret ? (React.createElement(IconButton, { bg: "transparent", "data-testid": "layer-item-toggle", hoverBg: "transparent", onClick: onCollapse },
            React.createElement(Box, { transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0)' },
                React.createElement(CaretDownIcon, { color: isSelected ? 'white' : 'black', width: "8px" })))) : (React.createElement(Box, { height: 26, width: 26 })),
        React.createElement(Flex, { alignItems: "center" },
            isClipped && React.createElement(LayerClippedArrowIcon, { ml: 2 }),
            React.createElement(LayerIcon, { isCollapsed: isCollapsed, item: item })),
        React.createElement(Box, { flex: 1, minWidth: "1px" }, isFocus ? (React.createElement(Input, { bg: isFocus ? 'white' : 'transparent', border: "none", fontSize: fontSize, fontWeight: fontWeight, height: inputHeight, kind: "custom", lineHeight: 1, onBlur: handleOnBlur, onFocus: handleOnFocus, onKeyPress: handleOnKeyPress, pl: 1, ref: inputRef, value: name, width: 1 })) : (React.createElement(Text, { cursor: "default", "data-testid": "layer-name", fontSize: fontSize, fontWeight: fontWeight, ml: 1, truncated: true, userSelect: "none", width: 1 },
            React.createElement(Highlighter, { searchWords: [filterText], textToHighlight: name }))))));
}
export default React.memo(Layer);
var styleOverrides = {
    symbol: {
        default: {
            bg: 'transparent',
            color: 'purple',
            hoverBg: 'snowDark',
        },
        selected: {
            bg: 'purple',
            color: 'white',
            hoverBg: 'purple',
        },
    },
};
